import {Scene, SceneActivationParams, SceneMountParams, SceneParams} from "./Graphic";
import {Subscription} from "rxjs";
import * as d3 from "d3";
import {debounceTime, withLatestFrom} from "rxjs/operators";


export function trigger(pos: number) {
    return function (target, propertyKey, descriptor?) {
        target.fmap = target.fmap || {}
        target.fmap[pos] = propertyKey
    }
}

type TriggerSceneParams = {
    data?: any;
}



let MARGINS = {top: 100, right: window.innerWidth / 4, bottom: 100, left: window.innerWidth / 4}
let width = window.innerWidth - MARGINS.left - MARGINS.right;
let height = window.innerHeight - MARGINS.top - MARGINS.bottom;

export class TriggerScene implements Scene {
    public screenLengths = 0;
    public graphicContainer: HTMLDivElement;

    private selections: {[id: string]: any} = {};
    private lastTrig = undefined;
    private data = null;
    private scales: {[id: string]: any} = {};
    private shapes: {[id: string]: any} = {};

    constructor(p: SceneParams & TriggerSceneParams) {
        this.screenLengths = p.screenLengths;
        this.data = p.data;

        this.graphicContainer = document.createElement("div");
        this.graphicContainer.classList.add("gfx-container")
    }

    public activate(params: SceneActivationParams): Subscription[] {
        let subs = [];

        subs.push(params.resize$.subscribe(r => {
            MARGINS = {top: 100, right: window.innerWidth / 4, bottom: 100, left: window.innerWidth / 4}
            width = window.innerWidth - MARGINS.left - MARGINS.right;
            height = window.innerHeight - MARGINS.top - MARGINS.bottom;
            this.scales.airY = this.scales.airY.range([height, 0])
            this.scales.airYLog = this.scales.airYLog.range([height, 0])
            this.scales.airX = this.scales.airX.range([0, width])

            this.scales.bllY = this.scales.bllY.range([height, 0])
            this.scales.bllX = this.scales.bllX.range([0, width])

            this.selections.axBllX.interrupt().transition().attr("transform", `translate(0, ${height})`)
            let offsetY = 70;
            if (window.isMobile.any) {
                offsetY = 60
            }
            this.selections.yUnits.attr("y", -offsetY - 15).attr("x", -height/2).attr("transform", "rotate(-90)")
            this.selections.ugDl.attr("y", -offsetY).attr("x", -height/2).attr("transform", "rotate(-90)")
            this.selections.milligramUnits.attr("y", -offsetY).attr("x", -height/2).attr("transform", "rotate(-90)")

            this.selections.svg.attr("width", `${r.innerWidth}px`);
            this.selections.svg.attr("height", `${r.innerHeight}px`);
            this.selections.gMargins.attr("transform", `translate(${MARGINS.left}, ${MARGINS.top})`)
        }))

        // console.log(this)

        const trigs = Object.keys(Object.getPrototypeOf(this).fmap).filter(x => x !== "init").map(x => parseFloat(x))

        subs.push(params.progress$.pipe(debounceTime(100)).subscribe(p => {
            const currentScreen = p * (this.screenLengths) - .5;
            if (currentScreen < .01) {
                if (this.lastTrig != null) {
                    this.init();
                    this.lastTrig = null;
                }

            } else {
                try {
                    const t = trigs.filter(x => x >= currentScreen)
                    if (t[0] !== this.lastTrig) {
                        this[Object.getPrototypeOf(this).fmap[t[0]]]()
                        this.lastTrig = t[0]
                    }
                } catch (e) {
                    console.error(e)
                }
            }
        }))
        return subs;
    }

    public mount(p: SceneMountParams): void {
        this.scales.airY = d3.scaleLinear().domain([0, 3000]).range([height, 0])
        this.scales.airYLog = d3.scaleLog().domain([1, d3.max(this.data.airData, d => <number>(<any>d).result)]).range([height, 0])
        this.scales.airX = d3.scaleTime().domain([d3.isoParse("2013-01-01"), d3.isoParse("2020-01-01")]).range([0, width])

        this.scales.bllY = d3.scaleLinear().domain([0, 60]).range([height, 0])
        this.scales.bllX = d3.scaleTime().domain([d3.isoParse("1985-01-01"), d3.isoParse("2017-12-31")]).range([0, width])
        this.selections.photo = d3.select(p.vizPlate).append("div").classed("hidden-photo", true).classed("dumeus-photo", true)

        const svg = d3.select(p.vizPlate).append("svg")
        this.selections["svg"] = svg;
        this.selections["gMargins"] = svg.append("g").classed("margins", true)
        this.selections["gAxes"] = this.selections.gMargins.append("g").classed("axes", true)
        this.selections["gBloodTests"] = this.selections["gMargins"].append("g").classed("blood-tests", true)
        this.selections["gAirTests"] = this.selections["gMargins"].append("g").classed("air-tests", true)

        let offsetY = 70;
        if (window.isMobile.any) {
            offsetY = 60
        }


        this.selections.yUnits = this.selections.gAirTests.append("text").classed("y-units", true).text("MICROGRAMS PER CUBIC METER").attr("y", -offsetY - 15).attr("x", -height/2).attr("transform", "rotate(-90)").attr("opacity", 0);
        this.selections.ugDl = this.selections.gBloodTests.append("text").classed("y-units", true).text("MICROGRAMS PER DECILITER").attr("y", -offsetY).attr("x", -height/2).attr("transform", "rotate(-90)").attr("opacity", 0);
        this.selections.milligramUnits = this.selections.gBloodTests.append("text").classed("y-units", true).text("MILLIGRAMS").attr("y", -offsetY).attr("x", -height/2).attr("transform", "rotate(-90)").attr("opacity", 0);



        this.shapes.mucLine = d3.line().x(d => this.scales.airX(d.date)).y(d => this.scales.airY(d.result))
        this.shapes.mucLineLog = d3.line().x(d => this.scales.airX(d.date)).y(d => this.scales.airYLog(d.result))

        this.scales.mgScale = d3.scaleLinear().domain([0, 1000]).range([height, 0])

        this.selections["mucLine"] = this.selections.gAirTests.append("path").classed("resp-muc", true).datum([
            {date: d3.isoParse("2013-01-01"), result: 2500},
            {date: d3.isoParse("2020-01-01"), result: 2500},
        ]).attr("d", this.shapes.mucLine)

        this.shapes.cdcLine = d3.line().x(d => this.scales.bllX(d.date)).y(d => this.scales.bllY(d.bll))
        this.selections["cdcLine"] = this.selections.gBloodTests.append("path").classed("cdc-limit", true).datum([
            {date: d3.isoParse("1985-01-01"), bll: 5},
            {date:  d3.isoParse("2017-12-31"), bll: 5},
        ]).attr("d", this.shapes.cdcLine)

        let len = this.selections.cdcLine.node().getTotalLength()
        this.selections.cdcLine.attr("stroke-dasharray", `${len} ${len}`).attr("stroke-dashoffset", len);

        this.shapes.axAirY = d3.axisLeft(this.scales.airY).ticks(4).tickFormat(d => d3.format(",")(d))
        this.selections.axAirY = this.selections.gAxes.append("g").classed("air-y-ax", true).classed("ax", true).call(this.shapes.axAirY)

        this.shapes.axBllX = d3.axisBottom(this.scales.bllX).ticks(4)
        this.selections.axBllX = this.selections.gAxes.append("g").classed("bll-x-ax", true).classed("ax", true).call(this.shapes.axBllX).attr("transform", `translate(0, ${height})`).attr("opacity", 0);

        this.shapes.axBllY = d3.axisLeft(this.scales.bllY).ticks(4)
        this.selections.axBllY = this.selections.gAxes.append("g").classed("bll-y-ax", true).classed("ax", true).call(this.shapes.axBllY)


        this.selections["bloodTests"] = this.selections["gBloodTests"].selectAll("circle").filter(function() {
            return this.classList.contains("blood-test");
        }).data(this.data.bllData, d => d.id).enter().append("circle").classed("blood-test", true).attr("r", 3)
            .attr("cx", d => this.scales.bllX(d.date))
            .attr("cy", d => this.scales.bllY(d.bll));

        this.selections["airTests"] = this.selections["gAirTests"].selectAll("circle").filter(function() {
            return this.classList.contains("air-test");
        }).data(this.data.airData, d => d.id).enter().append("circle").classed("air-test", true).attr("r", 3)
            .attr("cx", d => this.scales.airX(d.date))
            .attr("cy", this.handleLogNaN.bind(this));

        this.init()
    }

    @trigger(3)
    @trigger(7)
    init() {
        this.selections.bloodTests.interrupt().transition().attr("cx", d => this.scales.bllX(d.date)).attr("cy", window.innerHeight)
        this.selections.airTests.interrupt().transition().attr("cx", d => this.scales.airX(d.date)).attr("cy", window.innerHeight)
        this.selections.axAirY.interrupt().transition().attr("opacity", 0).call(this.shapes.axAirY)
        let lineLen = this.selections.mucLine.node().getTotalLength()
        this.selections.mucLine.interrupt().attr("d", this.shapes.mucLine)
            .attr("stroke-dasharray", `${lineLen} ${lineLen}`)
            .attr("stroke-dashoffset", lineLen)
        this.selections.axBllX.interrupt().transition().attr("opacity", 0).call(this.shapes.axBllX)
        this.selections.axBllY.interrupt().transition().attr("opacity", 0).call(this.shapes.axBllY)

        this.selections.photo.node().classList.add("hidden-photo")
        this.selections.photo.node().classList.remove("fade-in")

        this.selections.yUnits.attr("opacity", 0)
        this.selections.ugDl.attr("opacity", 0)
        this.selections.milligramUnits.attr("opacity", 0)
        let len = this.selections.cdcLine.node().getTotalLength()
        this.selections.cdcLine.interrupt().transition().attr("d", this.shapes.cdcLine).attr("stroke-dasharray", `${len} ${len}`).attr("stroke-dashoffset", len);
    }

    @trigger(1)
    bringInAirSamples() {
        this.shapes.axAirY = d3.axisLeft(this.scales.airYLog).ticks(4).tickFormat(d => d3.format(",")(d))
        this.selections.axAirY.interrupt().transition().attr("opacity", 1).call(this.shapes.axAirY)
        this.selections.airTests.interrupt().transition().delay((d, i) => i * 4).attr("cx", d => this.scales.airX(d.date)).attr("cy", this.handleLogNaN.bind(this))
            .attr("opacity", 1)
        let len = this.selections.mucLine.node().getTotalLength();
        this.selections.mucLine.interrupt().transition().attr("d", this.shapes.mucLineLog).attr("stroke-dasharray", `${len} ${len}`).attr("stroke-dashoffset", len)
        this.selections.axBllX.interrupt().transition().attr("opacity", 0).call(this.shapes.axBllX)
        this.selections.axBllY.interrupt().transition().attr("opacity", 0).call(this.shapes.axBllY)
        this.selections.yUnits.interrupt().transition().attr("opacity", 1)

    }

    @trigger(2)
    highlightOverMUC() {
        this.shapes.axAirY = d3.axisLeft(this.scales.airYLog).ticks(4).tickFormat(d => d3.format(",")(d))
        this.selections.axAirY.interrupt().transition().attr("opacity", 1).call(this.shapes.axAirY)
        this.selections.airTests.interrupt().transition().delay((d, i) => i * 4).attr("cx", d => this.scales.airX(d.date)).attr("cy", this.handleLogNaN.bind(this))
            .attr("opacity", d => d.exceeds_8hr_muc ? 1 : .1)
        this.selections.mucLine.interrupt().transition().attr("d", this.shapes.mucLineLog).attr("stroke-dashoffset", 0)
        this.selections.axBllX.interrupt().transition().attr("opacity", 0).call(this.shapes.axBllX)

        this.selections.axBllY.interrupt().transition().attr("opacity", 0).call(this.shapes.axBllY)
        this.selections.yUnits.interrupt().transition().attr("opacity", 1)

    }

    @trigger(4)
    showBllXAxis() {
        this.selections.bloodTests.interrupt().transition().attr("cx", d => this.scales.bllX(d.date)).attr("cy", window.innerHeight)
        this.selections.axBllX.interrupt().transition().attr("opacity", 1).call(this.shapes.axBllX)
        this.selections.axBllY.interrupt().transition().attr("opacity", 0).call(this.shapes.axBllY)
        this.selections.yUnits.interrupt().transition().attr("opacity", 0)
        this.selections.ugDl.interrupt().transition().attr("opacity", 0)
        let len = this.selections.cdcLine.node().getTotalLength()
        this.selections.cdcLine.interrupt().transition().attr("stroke-dasharray", `${len} ${len}`).attr("stroke-dashoffset", len);
    }

    @trigger(5)
    @trigger(6)
    showBloodTests() {
        this.selections.bloodTests.interrupt().transition().delay((d, i) => i * 4).attr("cx", d => this.scales.bllX(d.date)).attr("cy", d => this.scales.bllY(d.bll)).attr("opacity", 1)
        this.selections.axBllX.interrupt().transition().attr("opacity", 1).call(this.shapes.axBllX)
        this.selections.axBllY.interrupt().transition().attr("opacity", 1).call(this.shapes.axBllY)

        this.selections.ugDl.interrupt().transition().attr("opacity", 1)
        this.selections.milligramUnits.interrupt().transition().attr("opacity", 0);
        this.selections.cdcLine.interrupt().transition().attr("stroke-dashoffset", 0);
    }

    // @trigger(12)
    @trigger(8)
    @trigger(9)
    @trigger(10)
    showDumeusPhoto() {
        this.selections.photo.node().classList.remove("hidden-photo")
        setTimeout(() => this.selections.photo.node().classList.add("fade-in"));
    }



    private handleLogNaN(d) {
        let r = this.scales.airYLog(d.result)
        if (Number.isNaN(r)) {
            return height + 10
        }
        return r
    }



    public deactivate(subs: Subscription[]) {
        for (let sub of subs) {
            sub.unsubscribe();
        }
    }
}